import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { fetchAll } from '@/lib/paginate'
import { useHousehold } from '@/context/HouseholdContext'
import { accountBalance, type FinanceTx, type Rates } from '@/lib/finance'
import { latestRates, useBalances } from '@/hooks/useAccounts'
import { useNetWorthItems } from '@/hooks/useNetWorth'
import type { Account, ExchangeRate, NetWorthItem } from '@/types/db'

export interface Snapshot {
  month: string // 'YYYY-MM-01' — a foto é do FIM deste mês
  accounts_base: number
  cards_base: number // negativo = dívida
  items_base: number | null // null = sem histórico (ver comentário abaixo)
  total_base: number | null
}

// Os ITENS de patrimônio (imóveis, empréstimos, FGTS) guardam só o valor ATUAL —
// não há série temporal. Nos meses passados o valor de HOJE foi repetido (decisão
// consciente do usuário): eles mudam pouco, então a variação real do passado vem
// de contas/cartões, e uma linha constante é mais útil que uma lacuna. Mas isso
// NÃO é medição histórica — a tela diz isso em voz alta.
export function useSnapshots() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['snapshots', householdId],
    queryFn: async (): Promise<Snapshot[]> => {
      const { data, error } = await supabase
        .from('net_worth_snapshots')
        .select('month, accounts_base, cards_base, items_base, total_base')
        .eq('household_id', householdId)
        .order('month')
      if (error) throw error
      return (data ?? []).map((s) => ({
        month: s.month as string,
        accounts_base: Number(s.accounts_base),
        cards_base: Number(s.cards_base),
        items_base: s.items_base == null ? null : Number(s.items_base),
        total_base: s.total_base == null ? null : Number(s.total_base),
      }))
    },
  })
}

function monthKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}
function convert(amount: number, currency: string, rates: Rates): number {
  // Sem taxa não dá para somar na base — 0 é melhor que um número inventado.
  const r = rates?.get(currency)
  if (r == null) return 0
  return Math.round(amount * r * 100) / 100
}

// Mantém a "fotografia" do MÊS CORRENTE sempre atualizada (upsert a cada abertura
// do app). Quando o mês vira, essa linha congela com o último valor visto nele —
// que é justamente a foto do fim do mês. Sem cron, sem job: o próprio uso mantém.
export function useKeepSnapshot() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  // Sinais de mudança: editar um bem/dívida (netWorthItems) ou lançar/apagar uma
  // transação (balances é invalidado pelas mutações) refaz a foto do mês corrente
  // na hora — sem precisar recarregar a página.
  const { data: itemsSignal } = useNetWorthItems()
  const { data: balancesSignal } = useBalances()

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // A base precisa ser conhecida ANTES de buscar as taxas (o filtro por
        // `quote` depende dela) — senão uma taxa de quote errada entraria na foto.
        const { data: hh } = await supabase
          .from('households')
          .select('base_currency')
          .eq('id', householdId)
          .single()
        const base = (hh?.base_currency as string) ?? 'GTQ'

        const [{ data: accounts }, { data: items }, { data: rateRows }, txs] = await Promise.all([
          supabase.from('accounts').select('*').eq('household_id', householdId).eq('archived', false),
          supabase.from('net_worth_items').select('*').eq('household_id', householdId).eq('archived', false),
          supabase
            .from('exchange_rates')
            .select('base, quote, date, rate')
            .eq('quote', base)
            .order('date', { ascending: false }),
          fetchAll<FinanceTx>((from, to) =>
            supabase
              .from('transactions')
              .select('account_id, kind, amount, status, currency, amount_account, date')
              .eq('household_id', householdId)
              .range(from, to),
          ),
        ])
        if (cancelled || !accounts) return

        const rates = latestRates((rateRows ?? []) as ExchangeRate[], base)

        let accountsBase = 0
        let cardsBase = 0
        for (const a of accounts as Account[]) {
          const mine = (txs ?? []).filter((t) => t.account_id === a.id)
          // Conta: só PAGO. Cartão: todos os status — compra lançada já é dívida real.
          const bal = accountBalance(a, mine, rates, { onlyPaid: a.type !== 'cartao' })
          const conv = convert(bal, a.currency, rates)
          if (a.type === 'cartao') cardsBase += conv
          else accountsBase += conv
        }

        let itemsBase = 0
        for (const i of (items ?? []) as NetWorthItem[]) {
          const conv = convert(Number(i.value), i.currency, rates)
          itemsBase += i.kind === 'asset' ? conv : -conv
        }

        const row = {
          household_id: householdId,
          month: monthKey(new Date()),
          accounts_base: accountsBase,
          cards_base: cardsBase,
          items_base: itemsBase,
          total_base: accountsBase + cardsBase + itemsBase,
        }
        await supabase.from('net_worth_snapshots').upsert(row, { onConflict: 'household_id,month' })
        if (!cancelled) void qc.invalidateQueries({ queryKey: ['snapshots'] })
      } catch (e) {
        // Best-effort: a foto do mês não pode derrubar o app se o fetch falhar.
        console.warn('[useKeepSnapshot] falha ao atualizar snapshot:', e)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [householdId, itemsSignal, balancesSignal])
}
